// : the base color #e0df1f only returns lime, if the previous color family was lime
import chroma from 'chroma-js';
import tailwindColors from '../../TailwindColors'; // Import your tailwind colors or paste them here


export const generatePaletteFromColor = (baseColor) => {
    // Return object with palette and metadata
    const result = {
        palette: {},
        closestFamily: '',
        mappedShade: '500',
        isGrayscale: false
    };

    const hexToRgb = (hex, currentRgb = { r: 0, g: 0, b: 0 }) => {
        if (!hex || typeof hex !== 'string') {
            console.error('Invalid hex value:', hex);
            return currentRgb;
        }

        try {
            const [r, g, b] = chroma(hex).rgb();
            return {
                r: isNaN(r) ? currentRgb.r : r,
                g: isNaN(g) ? currentRgb.g : g,
                b: isNaN(b) ? currentRgb.b : b
            };
        } catch (e) {
            console.error('Chroma failed to parse hex:', hex, e);
            return currentRgb;
        }
    };


    const hexToHsl = (hex, currentHsl = { h: 0, s: 0, l: 0 }) => {
        try {
            const color = chroma(hex);
            let [h, s, l] = color.hsl();

            // If hue is NaN (happens when s=0 or l=0 or l=1), use the current hue value instead of 0
            if (isNaN(h)) h = currentHsl.h;

            return { h, s, l };
        } catch (e) {
            console.error('Invalid hex value for HSL conversion:', hex, e);
            return currentHsl;
        }
    };

    const hslToHex = (h, s, l, currentHsl = { h: 0, s: 0, l: 0 }) => {
        try {
            // Use currentHsl values as fallback if any component is NaN
            const safeH = isNaN(h) ? currentHsl.h : h;
            const safeS = isNaN(s) ? currentHsl.s : s;
            const safeL = isNaN(l) ? currentHsl.l : l;

            const hex = chroma.hsl(safeH, safeS, safeL).hex();
            return hex;
        } catch (e) {
            console.error('Invalid HSL values:', { h, s, l }, e);
            return '#000000';
        }
    };

    const colorDistance = (color1, color2) => {
        const rgb1 = hexToRgb(color1);
        const rgb2 = hexToRgb(color2);

        if (!rgb1 || !rgb2) return Infinity;

        return Math.sqrt(
            Math.pow(rgb1.r - rgb2.r, 2) +
            Math.pow(rgb1.g - rgb2.g, 2) +
            Math.pow(rgb1.b - rgb2.b, 2)
        );
    };

    // Color family ranges
    const colorFamilyRanges = [
        { name: 'red', hueRange: [355, 10] },
        { name: 'orange', hueRange: [10, 40] },
        { name: 'amber', hueRange: [40, 49] },
        { name: 'yellow', hueRange: [50, 64] },
        { name: 'lime', hueRange: [65, 90] },
        { name: 'green', hueRange: [90, 140] },
        { name: 'emerald', hueRange: [140, 160] },
        { name: 'teal', hueRange: [160, 180] },
        { name: 'cyan', hueRange: [180, 195] },
        { name: 'sky', hueRange: [195, 210] },
        { name: 'blue', hueRange: [210, 240] },
        { name: 'indigo', hueRange: [240, 260] },
        { name: 'violet', hueRange: [260, 275] },
        { name: 'purple', hueRange: [275, 290] },
        { name: 'fuchsia', hueRange: [290, 320] },
        { name: 'pink', hueRange: [320, 335] },
        { name: 'rose', hueRange: [335, 355] },
        // Add grayscale colors
        { name: 'slate', hueRange: [-1, -1], isGrayscale: true },
        { name: 'gray', hueRange: [-1, -1], isGrayscale: true },
        { name: 'zinc', hueRange: [-1, -1], isGrayscale: true },
        { name: 'neutral', hueRange: [-1, -1], isGrayscale: true },
        { name: 'stone', hueRange: [-1, -1], isGrayscale: true },
    ];

    // Function to check if a color is grayscale (very low saturation)
    const isGrayscale = (hex) => {
        // First check using our HSL conversion
        const { s } = hexToHsl(hex);

        // Also check using RGB values as a backup
        const rgb = hexToRgb(hex);
        const maxDiff = Math.max(
            Math.abs(rgb.r - rgb.g),
            Math.abs(rgb.r - rgb.b),
            Math.abs(rgb.g - rgb.b)
        );

        const isTrulyGrayscale = s < 0.00 && maxDiff < 15;

        return isTrulyGrayscale;
    };

   
    // nienke 8/4/2025
    // you updated this because on some colors like #e0df1f the output in palette was not consistent, but you
    // had to keep the 'stick to previous color' behaviour for when you are making only smalll changes
    const findClosestFamily = (baseHex, previousFamily = null, forceFamilyUpdate = false) => {
        const inputHsl = hexToHsl(baseHex);
        const inputHue = inputHsl.h;
        // const inputSaturation = inputHsl.s;

        // Initialize color cache if it doesn't exist
        if (typeof window !== 'undefined' && !window._exactColorFamilyCache) {
            window._exactColorFamilyCache = {};
        }

        // CRITICAL: For exact colors, always return the same family
        // This ensures consistency for the same exact hex value
        if (typeof window !== 'undefined' && window._exactColorFamilyCache[baseHex] && !forceFamilyUpdate) {
            // console.log(`Using cached family ${window._exactColorFamilyCache[baseHex]} for exact color ${baseHex}`);
            // Update last family in the tracker to maintain consistency
            if (window._colorFamilyTracker) {
                window._colorFamilyTracker.lastFamily = window._exactColorFamilyCache[baseHex];
            }
            return window._exactColorFamilyCache[baseHex];
        }

        // Check if the color is grayscale
        if (isGrayscale(baseHex)) {
            result.isGrayscale = true;
            // For grayscale colors, use the previous family if available, otherwise default to "gray"
            const family = previousFamily && colorFamilyRanges.some(f => f.name === previousFamily && f.isGrayscale)
                ? previousFamily
                : "gray";

            // Cache this result for consistency
            if (typeof window !== 'undefined') {
                window._exactColorFamilyCache[baseHex] = family;
            }
            return family;
        }

        // Store a map of similar color families that shouldn't flicker between each other
        const similarFamilies = {
            'lime': ['yellow', 'green'],
            'yellow': ['lime', 'amber'],
            'green': ['lime', 'emerald'],
            'amber': ['yellow', 'orange'],
            'orange': ['amber', 'red'],
            'red': ['orange', 'rose'],
            'rose': ['red', 'pink'],
            'pink': ['rose', 'fuchsia'],
            'fuchsia': ['pink', 'purple'],
            'purple': ['fuchsia', 'violet'],
            'violet': ['purple', 'indigo'],
            'indigo': ['violet', 'blue'],
            'blue': ['indigo', 'sky'],
            'sky': ['blue', 'cyan'],
            'cyan': ['sky', 'teal'],
            'teal': ['cyan', 'emerald'],
            'emerald': ['teal', 'green']
        };

        // Find what the "natural" family would be based on hue alone
        let naturalFamily = null;
        for (const family of colorFamilyRanges) {
            if (family.isGrayscale) continue;

            const [min, max] = family.hueRange;
            if ((min <= max && inputHue >= min && inputHue <= max) ||
                (min > max && (inputHue >= min || inputHue <= max))) {
                naturalFamily = family.name;
                break;
            }
        }

        let selectedFamily;

        // If we have a previous family and not forcing update, check if we should stick with it
        if (previousFamily && !forceFamilyUpdate) {
            // If the previous family is the natural family or similar to it, keep using it
            if (previousFamily === naturalFamily ||
                (similarFamilies[previousFamily] && similarFamilies[previousFamily].includes(naturalFamily))) {
                selectedFamily = previousFamily;
            }
            // Special case: for lime/yellow boundary (around hue 65)
            else if ((previousFamily === 'lime' || previousFamily === 'yellow') &&
                Math.abs(inputHue - 65) < 5) {
                selectedFamily = previousFamily;
            }
            else {
                // Otherwise use the natural family
                selectedFamily = naturalFamily;
            }
        } else {
            // No previous family, use the natural family
            selectedFamily = naturalFamily;
        }

        // If no family was selected yet (shouldn't happen with proper ranges), calculate scores
        if (!selectedFamily) {
            // Calculate a score for each color family
            const familyScores = {};

            colorFamilyRanges.forEach(range => {
                // Skip grayscale families for normal colors
                if (range.isGrayscale) return;

                // Calculate distance from hue to the center of this family's range
                let hueDist;
                const [min, max] = range.hueRange;
                const centerHue = min > max
                    ? (min + max + 360) / 2 % 360  // Handle red's boundary crossing
                    : (min + max) / 2;

                // Calculate circular distance on the hue wheel
                hueDist = Math.min(
                    Math.abs(inputHue - centerHue),
                    360 - Math.abs(inputHue - centerHue)
                );

                // Create a score - lower is better
                // The further from center, the worse the score
                const hueScore = 1 - (hueDist / 180);  // Normalize to 0-1

                // Get the "ideal" color from this family at the current lightness
                const idealColor = getIdealColorFromFamily(range.name, inputHsl.l);

                // Calculate RGB distance as additional factor
                const rgbDist = colorDistance(baseHex, idealColor);
                const rgbScore = 1 - Math.min(rgbDist / 255, 1);  // Normalize to 0-1

                // Combined score with higher weight on hue for consistency
                familyScores[range.name] = (hueScore * 0.7) + (rgbScore * 0.3);
            });

            // Find the family with the highest score
            let bestFamily = Object.keys(familyScores)[0];
            let bestScore = familyScores[bestFamily];

            Object.entries(familyScores).forEach(([family, score]) => {
                if (score > bestScore) {
                    bestScore = score;
                    bestFamily = family;
                }
            });

            selectedFamily = bestFamily;
        }

        // Cache this exact color to family mapping for future consistency
        if (typeof window !== 'undefined') {
            window._exactColorFamilyCache[baseHex] = selectedFamily;
        }

        return selectedFamily;
    };













    // Helper function to get an "ideal" color from a family at a specific lightness
    const getIdealColorFromFamily = (family, targetLightness) => {
        // Find the shade in this family with lightness closest to target
        let closestShade = '500';
        let minDiff = Math.abs(hexToHsl(tailwindColors[family]['500']).l - targetLightness);

        Object.entries(tailwindColors[family]).forEach(([shade, color]) => {
            const diff = Math.abs(hexToHsl(color).l - targetLightness);
            if (diff < minDiff) {
                minDiff = diff;
                closestShade = shade;
            }
        });

        return tailwindColors[family][closestShade];
    };

    // Extract color patterns from a family
    const extractColorPattern = (family) => {
        const pattern = {};
        const baseHsl = hexToHsl(tailwindColors[family][500]);

        Object.entries(tailwindColors[family]).forEach(([shade, color]) => {
            const hsl = hexToHsl(color);

            pattern[shade] = {
                h: hsl.h,
                h_diff: hsl.h - baseHsl.h,
                s: hsl.s,
                s_ratio: baseHsl.s !== 0 ? hsl.s / baseHsl.s : 1,
                l: hsl.l,
                l_ratio: baseHsl.l !== 0 ? hsl.l / baseHsl.l : 1
            };
        });

        return pattern;
    };

    // Find the best shade to map the input color to based on lightness
    // debug it:
    // Add extensive logging to findBestShadeMapping
    const findBestShadeMapping = (inputColor, family) => {
        // console.group(`Mapping color ${inputColor} to shade in family ${family}`);
        const inputHsl = hexToHsl(inputColor);
        // console.log(`Input HSL: h=${inputHsl.h.toFixed(2)}, s=${(inputHsl.s * 100).toFixed(2)}%, l=${(inputHsl.l * 100).toFixed(2)}%`);

        let closestShade = '500';
        let minLightnessDiff = Math.abs(hexToHsl(tailwindColors[family][500]).l - inputHsl.l);
        // console.log(`Initial closest shade: 500 with diff ${minLightnessDiff.toFixed(4)}`);

        Object.entries(tailwindColors[family]).forEach(([shade, color]) => {
            const shadeHsl = hexToHsl(color);
            const diff = Math.abs(shadeHsl.l - inputHsl.l);
            // console.log(`Shade ${shade}: l=${(shadeHsl.l * 100).toFixed(2)}%, diff=${diff.toFixed(4)}${diff < minLightnessDiff ? ' (new best)' : ''}`);

            if (diff < minLightnessDiff) {
                minLightnessDiff = diff;
                closestShade = shade;
            }
        });

        // console.log(`Selected shade: ${closestShade}`);
        return closestShade;
    };

    

    // --- Main palette generation logic ---

    // Get HSL of input color
    const inputHsl = hexToHsl(baseColor);

    // Use a module-level variable to track the previous color and family
    // This is safer than localStorage but still maintains consistency
    if (typeof window !== 'undefined' && !window._colorFamilyTracker) {
        window._colorFamilyTracker = {
            lastColor: null,
            lastFamily: null,
            satHistory: []
        };
    }

    const tracker = typeof window !== 'undefined' ? window._colorFamilyTracker : { lastColor: null, lastFamily: null, satHistory: [] };

    // Check if this is a color adjustment or a completely new color
    let isAdjustment = false;
    let forceFamilyUpdate = false;

    if (tracker.lastColor) {
        const lastHsl = hexToHsl(tracker.lastColor);

        // If hue is similar (within 5 degrees), consider it an adjustment
        const hueDiff = Math.min(
            Math.abs(inputHsl.h - lastHsl.h),
            Math.abs(inputHsl.h - lastHsl.h + 360),
            Math.abs(inputHsl.h - lastHsl.h - 360)
        );

        isAdjustment = hueDiff < 5;

        // Track saturation history to detect major changes
        if (isAdjustment) {
            tracker.satHistory.push(inputHsl.s);
            if (tracker.satHistory.length > 5) tracker.satHistory.shift();

            // If saturation changed dramatically, force family update
            const oldAvgSat = tracker.satHistory.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
            const newAvgSat = tracker.satHistory.slice(-3).reduce((a, b) => a + b, 0) / 3;
            const satRatioChange = newAvgSat / (oldAvgSat || 0.01);

            forceFamilyUpdate = satRatioChange > 2 || satRatioChange < 0.5;

            if (forceFamilyUpdate) {
                // console.log('Major saturation change detected - forcing family update');
            }
        }
    }

    // Determine the family
    const family = findClosestFamily(baseColor, tracker.lastFamily, forceFamilyUpdate);
    result.closestFamily = family;

    // Update tracker
    if (typeof window !== 'undefined') {
        tracker.lastColor = baseColor;
        tracker.lastFamily = family;
    }

    // console.log('Selected family:', family);
    // console.log('Input color HSL:', inputHsl);

    // Extract the color pattern from the closest family
    const familyPattern = extractColorPattern(family);

    // Find best shade to map the input color to based on lightness
    const targetShade = findBestShadeMapping(baseColor, family);
    result.mappedShade = targetShade;

    // Get the HSL values of the matched shade in the original family
    const matchedShadeHsl = hexToHsl(tailwindColors[family][targetShade]);
    const sRatio = inputHsl.s / matchedShadeHsl.s;

    // CRITICAL: Save original hue for use in all shades
    const originalHue = inputHsl.h;

    Object.entries(familyPattern).forEach(([shade, pattern]) => {
        if (shade === targetShade) {
            result.palette[shade] = baseColor;
            return;
        }

        // FORCE the hue to stay exactly the same as the input color's hue
        let newH = originalHue;  // <-- Use input hue directly, ignore pattern hue

        // Use the pattern's saturation and lightness with adjustments
        let newS = pattern.s * sRatio;
        const newL = pattern.l;

        // Generate the hex color with FORCED hue preservation
        result.palette[shade] = hslToHex(newH, newS, newL, { h: originalHue, s: 0, l: 0 });

        // Let's verify the generated color has the expected hue
        const generatedHsl = hexToHsl(result.palette[shade]);
        if (Math.abs(generatedHsl.h - originalHue) > 1) {
            // console.warn(`WARNING: Shade ${shade} has hue ${generatedHsl.h} instead of ${originalHue}`);
        }
    });

    return result;
};